/**
 * Phase 4 slice 2 — PPE Compliance. Dashboard summary + list + record form.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Plus, HardHat, TrendingDown } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import PhotoCaptureField from '../../components/safety/PhotoCaptureField';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';

const PPE_ITEMS = ['Helmet', 'Safety Shoes', 'Hi-Vis Vest', 'Gloves', 'Safety Harness', 'Eye Protection', 'Hearing Protection', 'Respirator'];
import { formatDateTime as fmt } from '../../lib/format';
const pctTone = (p) => (p == null ? 'text-slate-400' : p >= 90 ? 'text-emerald-600' : p >= 80 ? 'text-amber-600' : 'text-red-600');

export default function PPECompliance() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [checks, setChecks] = useState([]);
  const [dash, setDash] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ location_on_site: '', workers_checked: '', notes: '' });
  const [items, setItems] = useState({});
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => {}); }, []);
  useEffect(() => { if (!projectId && projects.length) setProjectId(projects[0].id); }, [projects, projectId]);

  const load = () => {
    if (!projectId) return;
    apiGet(`/safety/ppe-checks?project_id=${projectId}`).then((d) => setChecks(d.ppe_checks || [])).catch(() => {});
    apiGet(`/safety/ppe-dashboard?project_id=${projectId}`).then(setDash).catch(() => {});
  };
  useEffect(load, [projectId]); // eslint-disable-line

  const livePct = useMemo(() => {
    let c = 0; let nc = 0;
    Object.values(items).forEach((v) => { c += Number(v.compliant || 0); nc += Number(v.non_compliant || 0); });
    return (c + nc) ? Math.round((100 * c) / (c + nc) * 10) / 10 : null;
  }, [items]);

  const openForm = () => { setForm({ location_on_site: '', workers_checked: '', notes: '' }); setItems({}); setPhotos([]); setOpen(true); };

  const submit = async () => {
    const items_checked = Object.entries(items)
      .filter(([, v]) => Number(v.compliant || 0) + Number(v.non_compliant || 0) > 0)
      .map(([item, v]) => ({ item, compliant_count: Number(v.compliant || 0), non_compliant_count: Number(v.non_compliant || 0) }));
    if (items_checked.length === 0) { toast.error('Record at least one PPE item'); return; }
    setSaving(true);
    try {
      await apiPost('/safety/ppe-checks', {
        ...form, project_id: projectId, workers_checked: Number(form.workers_checked || 0),
        items_checked, photos_base64: photos,
      });
      toast.success('PPE check recorded');
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const setItem = (item, key, val) => setItems({ ...items, [item]: { ...items[item], [key]: val } });

  return (
    <div>
      <PageHeader overline="Safety / HSE" title="PPE Compliance" subtitle="Spot-checks on protective equipment — target ≥ 80%"
        actions={(
          <div className="flex items-center gap-3">
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="ppe-project-select" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
            </select>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button onClick={openForm} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="ppe-record-btn"><Plus size={14} /> Record Check</button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="ppe-dialog">
                <DialogHeader><DialogTitle>Record PPE Check {livePct != null && <span className={`text-sm ${pctTone(livePct)}`}>· {livePct}%</span>}</DialogTitle>
                  <DialogDescription>Spot-check protective equipment — enter compliant vs non-compliant counts per item.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input value={form.location_on_site} onChange={(e) => setForm({ ...form, location_on_site: e.target.value })} placeholder="Location on site" className="px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="ppe-location" />
                    <input type="number" value={form.workers_checked} onChange={(e) => setForm({ ...form, workers_checked: e.target.value })} placeholder="Workers checked" className="px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="ppe-workers" />
                  </div>
                  <div className="border border-slate-200 rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><th className="text-left p-2">PPE Item</th><th className="p-2 w-24">Compliant</th><th className="p-2 w-24">Non-comp.</th></tr></thead>
                      <tbody>
                        {PPE_ITEMS.map((it) => (
                          <tr key={it} className="border-t border-slate-100">
                            <td className="p-2">{it}</td>
                            <td className="p-1"><input type="number" min="0" value={items[it]?.compliant || ''} onChange={(e) => setItem(it, 'compliant', e.target.value)} className="w-full px-2 py-1 border border-slate-200 rounded-sm text-center" data-testid={`ppe-c-${it.replace(/\s/g, '')}`} /></td>
                            <td className="p-1"><input type="number" min="0" value={items[it]?.non_compliant || ''} onChange={(e) => setItem(it, 'non_compliant', e.target.value)} className="w-full px-2 py-1 border border-slate-200 rounded-sm text-center" data-testid={`ppe-nc-${it.replace(/\s/g, '')}`} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Notes / violations" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                  <div>
                    <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Photos</div>
                    <PhotoCaptureField photos={photos} setPhotos={setPhotos} max={4} />
                  </div>
                </div>
                <DialogFooter>
                  <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm font-display font-bold rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e] disabled:opacity-60" data-testid="ppe-submit">{saving ? 'Saving…' : 'Save Check'}</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      />

      {/* Dashboard summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5" data-testid="ppe-dashboard-cards">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="kpi-overline mb-2 flex items-center gap-2"><HardHat size={12} /> Overall Compliance</div>
          <div className={`text-4xl font-display font-extrabold tabular-nums ${pctTone(dash?.overall_compliance_pct)}`}>{dash?.overall_compliance_pct != null ? `${dash.overall_compliance_pct}%` : '—'}</div>
          <div className="text-xs text-slate-500 mt-1">{dash?.total_checks || 0} checks recorded</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="kpi-overline mb-2 flex items-center gap-2"><TrendingDown size={12} /> Most Violated Items</div>
          <div className="space-y-1">
            {(dash?.most_violated_items || []).slice(0, 5).map((m, i) => (
              <div key={i} className="flex justify-between text-sm"><span className="text-slate-600">{m.item}</span><span className="font-display font-bold text-red-600">{m.non_compliant}</span></div>
            ))}
            {(!dash?.most_violated_items || dash.most_violated_items.length === 0) && <div className="text-xs text-slate-400">No violations recorded.</div>}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="kpi-overline mb-2">Worst Locations</div>
          <div className="space-y-1">
            {(dash?.worst_locations || []).map((l, i) => (
              <div key={i} className="flex justify-between text-sm"><span className="text-slate-600 truncate max-w-[140px]">{l.location}</span><span className={`font-display font-bold ${pctTone(l.avg_compliance_pct)}`}>{l.avg_compliance_pct}%</span></div>
            ))}
            {(!dash?.worst_locations || dash.worst_locations.length === 0) && <div className="text-xs text-slate-400">No data.</div>}
          </div>
        </div>
      </div>

      {/* Checks list */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table" data-testid="ppe-table">
          <thead><tr><th>Location</th><th>Checked</th><th className="text-center">Workers</th><th className="text-center">Compliance</th><th>By</th></tr></thead>
          <tbody>
            {checks.length === 0 ? <tr><td colSpan={5} className="text-center text-sm text-slate-400 py-10">No PPE checks recorded.</td></tr>
              : checks.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="text-[13px] font-display font-semibold">{c.location_on_site || '—'}</td>
                  <td className="text-xs text-slate-600">{fmt(c.checked_at)}</td>
                  <td className="text-center">{c.workers_checked}</td>
                  <td className="text-center"><span className={`font-display font-bold ${pctTone(c.compliance_pct)}`}>{c.compliance_pct != null ? `${c.compliance_pct}%` : '—'}</span></td>
                  <td className="text-xs text-slate-600">{c.checked_by}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
